import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Table } from 'react-bootstrap';
import PhoenixDocCard from 'components/base/PhoenixDocCard';
import { selectProjectResults } from '../project-details/data-access/project.reducer';
import { AbuseDataItem } from '../Dashboard/AbuseAI/AbuseCard';
import { collectSafetyRuns, isNewSafetySchema, pickLatestSafetyRun } from './aiSafety.utils';

const AISafetyTable = () => {
  const projectResults = useSelector(selectProjectResults);
  const [mlData, setMlData] = useState<any[]>([]);
  const [llmData, setLlmData] = useState<AbuseDataItem[]>([]);

  useEffect(() => {
    const runs = collectSafetyRuns(projectResults);
    const latest = runs[0] ?? pickLatestSafetyRun(projectResults);
    if (!latest) {
      setMlData([]);
      setLlmData([]);
      return;
    }

    if (isNewSafetySchema(latest)) {
      setMlData([]);
      setLlmData([]);
      return;
    }

    const abuseAi = latest as Record<string, unknown>;
    const ml = abuseAi.ML_ABUSE_DETECTION ?? abuseAi.ML_Abuse_Detection;
    if (Array.isArray(ml)) {
      setMlData(ml);
    } else {
      setMlData([]);
    }

    const llmAnalysis = abuseAi.LLM_ABUSE_ANALYSIS as Record<string, unknown> | undefined;
    const findings = llmAnalysis?.Findings;
    if (Array.isArray(findings)) {
      setLlmData(findings as AbuseDataItem[]);
    } else {
      setLlmData([]);
    }
  }, [projectResults]);

  const latest = runsSafe(projectResults);
  const showNewSchemaMessage = latest != null && isNewSafetySchema(latest);

  return (
    <div>
      {showNewSchemaMessage ? (
        <PhoenixDocCard className="mb-4">
          <PhoenixDocCard.Header title="AI Safety Detection" />
          <PhoenixDocCard.Body>
            <p className="text-muted mb-0">
              This project uses the new AI Safety schema. Open the AI Safety workbench tab for KPIs, charts, and
              evidence.
            </p>
          </PhoenixDocCard.Body>
        </PhoenixDocCard>
      ) : null}

      {mlData.length > 0 && (
        <PhoenixDocCard className="mb-4">
          <PhoenixDocCard.Header title="AI Safety Detection - ML Model" />
          <PhoenixDocCard.Body>
            <Table responsive striped bordered hover>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Severity Score</th>
                  <th>Risk</th>
                  <th>Action</th>
                  <th>Observation</th>
                </tr>
              </thead>
              <tbody>
                {mlData.map((item, index) => (
                  <tr key={index}>
                    <td>{item.Category}</td>
                    <td>{item['Severity Score']}</td>
                    <td>{item.Risk}</td>
                    <td>{item.Action}</td>
                    <td>{item.Observation}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </PhoenixDocCard.Body>
        </PhoenixDocCard>
      )}

      {llmData.length > 0 && (
        <PhoenixDocCard className="mb-4">
          <PhoenixDocCard.Header title="AI Safety Detection - LLM Model" />
          <PhoenixDocCard.Body>
            <Table responsive striped bordered hover>
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Severity Score</th>
                  <th>Severity Level</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {llmData.map((item, index) => (
                  <tr key={index}>
                    <td>{item.Category}</td>
                    <td>{item.Severity_Score}</td>
                    <td>{item.Severity_Level}</td>
                    <td>{item.Action}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </PhoenixDocCard.Body>
        </PhoenixDocCard>
      )}
    </div>
  );
};

function runsSafe(projectResults: unknown) {
  const runs = collectSafetyRuns(projectResults);
  return runs[0] ?? pickLatestSafetyRun(projectResults);
}

export default AISafetyTable;
